import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { MessageCircle, Clock, CheckCircle, AlertCircle, Send, Loader2 } from 'lucide-react';
import { api } from '../utils/api';

export function AdminSupportManagement() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadTickets();
  }, [filterStatus]);

  const loadTickets = async () => {
    try {
      const status = filterStatus === 'all' ? null : filterStatus;
      const response = await api.request(`/support/admin/all-requests${status ? `?status=${status}` : ''}`);
      setTickets(response.requests || []);
      setStats(response.stats || {});
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (ticketId, status = null) => {
    if (!responseText.trim() && !status) return;

    setResponding(true);
    try {
      await api.request(`/support/admin/respond/${ticketId}`, {
        method: 'POST',
        body: JSON.stringify({
          message: responseText,
          status: status || 'in_progress'
        })
      });

      alert('✅ Response sent successfully!');
      setResponseText('');
      setSelectedTicket(null);
      loadTickets();
    } catch (error) {
      alert('Failed to send response. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await api.request(`/support/admin/update-status/${ticketId}?status=${status}`, {
        method: 'PATCH'
      });
      loadTickets();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'resolved':
      case 'closed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-orange-100 text-orange-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Tickets</p>
                <p className="text-2xl font-bold">{stats?.total || 0}</p>
              </div>
              <MessageCircle className="w-10 h-10 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Open</p>
                <p className="text-2xl font-bold">{stats?.open || 0}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold">{stats?.in_progress || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Resolved</p>
                <p className="text-2xl font-bold">{stats?.resolved || 0}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['all', 'open', 'in_progress', 'resolved'].map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2 rounded-lg capitalize ${
              filterStatus === status
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status === 'all' ? 'All Tickets' : status.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Tickets List */}
      <Card>
        <CardHeader>
          <CardTitle>Support Tickets</CardTitle>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Tickets Found</h3>
              <p className="text-gray-600">No support tickets match the current filter.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(ticket.status)}
                        <h3 className="font-semibold">{ticket.subject}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(ticket.status)}`}>
                          {ticket.status.replace('_', ' ').toUpperCase()}
                        </span>
                        {ticket.source === 'ai_chat' && (
                          <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-800">
                            From AI Chat
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>{ticket.user_name}</strong> ({ticket.user_email})
                      </p>
                      <p className="text-sm text-gray-600 line-clamp-2">{ticket.message}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>Category: {ticket.category}</span>
                        <span>•</span>
                        <span>Created: {new Date(ticket.created_at).toLocaleDateString()}</span>
                        {ticket.responses && ticket.responses.length > 0 && (
                          <>
                            <span>•</span>
                            <span className="text-purple-600 font-medium">
                              {ticket.responses.length} response{ticket.responses.length !== 1 ? 's' : ''}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {ticket.status === 'open' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateStatus(ticket.id, 'in_progress');
                          }}
                          className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          Start
                        </button>
                      )}
                      {(ticket.status === 'open' || ticket.status === 'in_progress') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateStatus(ticket.id, 'resolved');
                          }}
                          className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{selectedTicket.subject}</h2>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(selectedTicket.status)}`}>
                      {selectedTicket.status.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-500">
                      Ticket #{selectedTicket.id.slice(0, 8)}
                    </span>
                    <span className="text-sm text-gray-500">•</span>
                    <span className="text-sm text-gray-500">
                      {selectedTicket.user_name} ({selectedTicket.user_email})
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-600 mb-1">Customer Message:</p>
                  <p className="whitespace-pre-wrap">{selectedTicket.message}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Created: {new Date(selectedTicket.created_at).toLocaleString()}
                  </p>
                </div>

                {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Response History:</h3>
                    {selectedTicket.responses.map((response, idx) => (
                      <div key={idx} className="bg-purple-50 p-4 rounded-lg mb-2">
                        <p className="text-sm font-medium text-purple-900 mb-1">
                          {response.admin_name} (Support Team)
                        </p>
                        <p className="whitespace-pre-wrap">{response.message}</p>
                        <p className="text-xs text-purple-600 mt-2">
                          {new Date(response.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Response Form */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Send Response:</h3>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Type your response to the customer..."
                    rows="4"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-600"
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleRespond(selectedTicket.id, 'in_progress')}
                      disabled={!responseText.trim() || responding}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {responding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Send & Mark In Progress
                    </button>
                    <button
                      onClick={() => handleRespond(selectedTicket.id, 'resolved')}
                      disabled={!responseText.trim() || responding}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Send & Resolve
                    </button>
                    <button
                      onClick={() => setSelectedTicket(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
